import React, { useCallback, useMemo, useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  useTheme,
  styled,
  Collapse,
} from '@mui/material';
import { useTheme as useAppTheme } from '../../contexts/ThemeContext';
import { useStore } from 'zustand';
import { useUnityBuildStore } from '../../stores/store';
import {
  useGetTotalFinalPriceCloseouts,
  useGetTotalFinalPriceChangeOrderCloseouts,
} from '../../hooks/useCloseoutHooks';
import { formatCurrency, formatNumber } from '../closeout/closeoutHelpers';
import { getMaterialBreakdownForReport } from '../closeout/extension/aggregationUtils';
import { downloadPDF } from './ChangeOrderReportPDF';
import GetAppIcon from '@mui/icons-material/GetApp';
import { SectionHeader } from './SectionHeader';
import TariffsAndDutiesClause from './TariffsAndDutiesClause';
import GlobalSupplyChainClause from './GlobalSupplyChainClause';
import StandardContractTerms from './StandardContractTerms';
import { ContractorCertification } from './ContractorCertification';
import { ClientAcceptance } from './ClientAcceptance';

// Global print styles to ensure proper page layout
const GlobalPrintStyles = `
  @media print {
    @page {
      size: letter;
      margin: 0.75in;
    }
    
    html, body {
      width: 100% !important;
      height: 100% !important;
      margin: 0 !important;
      padding: 0 !important;
      overflow: visible !important;
      font-size: 12pt !important; /* larger base font for print */
      line-height: 1.4 !important;
    }
    
    * {
      box-sizing: border-box !important;
      max-width: 100% !important;
      word-wrap: break-word !important;
      overflow-wrap: break-word !important;
      word-break: break-word !important; /* allow long words to break */
      hyphens: auto !important; /* automatic hyphenation */
      overflow: visible !important;
    }
    
    /* Ensure no fixed widths that could cause overflow */
    .report-container, .report-content {
      max-width: 100% !important;
      width: 100% !important;
      margin: 0 !important;
      padding: 0 !important;
    }
  }
`;

// Enhanced styled components
const ReportContainer = styled(Paper)(({ theme }) => {
  const { darkMode } = useAppTheme();
  return {
    padding: theme.spacing(4),
    backgroundColor: darkMode ? '#1a1a1a' : '#ffffff',
    color: theme.palette.text.primary,
    fontFamily: 'Arial, sans-serif',
    fontSize: '12px',
    lineHeight: 1.4,
    maxWidth: '8.5in',
    margin: '0 auto',
    minHeight: '11in',
    boxShadow: darkMode ? '0 4px 20px rgba(255,255,255,0.1)' : '0 4px 20px rgba(0,0,0,0.1)',
    overflow: 'hidden',
    wordWrap: 'break-word',
    overflowWrap: 'break-word',
    wordBreak: 'normal',

    '@media print': {
      boxShadow: 'none !important',
      margin: '0 !important',
      padding: '0.25in !important',
      backgroundColor: '#ffffff !important',
      color: '#333333 !important',
      maxWidth: 'none !important',
      width: '100% !important',
      minHeight: 'auto !important',
      overflow: 'visible !important',
      wordWrap: 'break-word !important',
      overflowWrap: 'break-word !important',
      wordBreak: 'normal !important',
      fontSize: '12pt !important',
      lineHeight: '1.4 !important',

      // Force light mode and allow content to flow naturally
      '& *': {
        backgroundColor: '#ffffff !important',
        color: '#333333 !important',
        maxWidth: '100% !important',
        wordWrap: 'break-word !important',
        overflowWrap: 'break-word !important',
        wordBreak: 'normal !important',
        boxSizing: 'border-box !important',
        overflow: 'visible !important',
      },

      // Allow content to flow naturally without clipping
      '& p, & li, & div, & span, & h1, & h2, & h3, & h4, & h5, & h6': {
        maxWidth: '100% !important',
        overflow: 'visible !important',
        wordWrap: 'break-word !important',
        overflowWrap: 'break-word !important',
        wordBreak: 'normal !important',
        fontSize: '11pt !important',
        lineHeight: '1.4 !important',
      },

      // Quill (RichTextEditor) specific tweaks for print – keep preview & print in sync
      '& .ql-editor': {
        fontSize: '11pt !important',
        lineHeight: '1.4 !important',
        color: '#333333 !important',
        maxWidth: '100% !important',
        wordWrap: 'break-word !important',
        overflowWrap: 'break-word !important',
        wordBreak: 'break-word !important',
      },

      '& .ql-editor ul, & .ql-editor ol': {
        paddingLeft: '1.25em !important',
        margin: '0 0 0.6em 0 !important',
      },

      '& .ql-editor li': {
        margin: '0.25em 0 !important',
      },

      // Ensure tables fit within page width but allow content to wrap
      '& table, & .MuiTable-root': {
        maxWidth: '100% !important',
        tableLayout: 'fixed !important' /* prevent cells expanding beyond page */,
        width: '100% !important',
        fontSize: '10pt !important',
      },

      // Make sections more compact for print
      '& .MuiCollapse-root, & .MuiBox-root': {
        margin: '0 !important',
        padding: '2px !important',
      },

      // Override specific dark mode elements
      '& .MuiTableCell-root': {
        backgroundColor: '#ffffff !important',
        color: '#333333 !important',
        borderColor: '#e0e0e0 !important',
      },

      '& .MuiTableHead-root .MuiTableCell-root': {
        backgroundColor: '#e3f2fd !important',
        color: '#333333 !important',
      },

      '& .total-row .MuiTableCell-root': {
        backgroundColor: '#e3f2fd !important',
        color: '#333333 !important',
      },
    },
  };
});

const DetailTable = styled(Table)(({ theme }) => ({
  '& .MuiTableCell-root': {
    fontSize: '10px',
    padding: theme.spacing(0.5, 1),
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.mode === 'dark' ? '#2c2c2c' : '#ffffff',
    color: theme.palette.text.primary,
    maxWidth: '200px',
    overflow: 'hidden',
    wordWrap: 'break-word',
    overflowWrap: 'break-word',
    wordBreak: 'normal',
    whiteSpace: 'normal',
  },
  '& .MuiTableHead-root .MuiTableCell-root': {
    fontWeight: 'bold',
    backgroundColor: theme.palette.mode === 'dark' ? '#404040' : '#e3f2fd',
    color: theme.palette.text.primary,
  },
  '& .total-row': {
    backgroundColor: theme.palette.mode === 'dark' ? '#404040' : '#e3f2fd',
    fontWeight: 'bold',
  },
  '& .total-row .MuiTableCell-root': {
    backgroundColor: theme.palette.mode === 'dark' ? '#404040' : '#e3f2fd',
    fontWeight: 'bold',
  },

  '@media print': {
    pageBreakInside: 'avoid',
    width: '100% !important',
    maxWidth: '100% !important',
    tableLayout: 'fixed !important',

    '& .MuiTableCell-root': {
      backgroundColor: '#ffffff !important',
      color: '#333333 !important',
      borderColor: '#e0e0e0 !important',
      fontSize: '10px !important',
      padding: '1px 2px !important',
      maxWidth: 'none !important',
      width: 'auto !important',
      overflow: 'visible !important',
      wordWrap: 'break-word !important',
      overflowWrap: 'break-word !important',
      wordBreak: 'normal !important',
      whiteSpace: 'normal !important',
      boxSizing: 'border-box !important',
      lineHeight: '1.2 !important',
      hyphens: 'auto !important',
    },

    '& .MuiTableHead-root .MuiTableCell-root': {
      backgroundColor: '#e3f2fd !important',
      color: '#333333 !important',
      fontWeight: 'bold',
    },

    '& .total-row .MuiTableCell-root': {
      backgroundColor: '#e3f2fd !important',
      color: '#333333 !important',
      fontWeight: 'bold',
    },

    '& tbody tr': {
      pageBreakInside: 'avoid',
    },
  },
}));

interface ChangeOrderReportProps {
  companyName?: string;
  workDescription?: string;
  pcoNumber?: string;
  showDetailedBreakdown?: boolean;
  includeLaborRates?: boolean;
  includeEquipmentCosts?: boolean;
  includeSubcontractorCosts?: boolean;
  includeGeneralExpenses?: boolean;
  showTaxDetails?: boolean;
  showOverheadMarkup?: boolean;
  showBaseCostColumn?: boolean;
  // New section visibility controls
  showMaterialsSection?: boolean;
  showQuotesSection?: boolean;
  showLaborSection?: boolean;
  showProjectTotalsBreakdown?: boolean;
  // Contractual Clauses
  showGlobalSupplyChainClause?: boolean;
  showTariffsAndDutiesClause?: boolean;
  showStandardContractTerms?: boolean;
  // Extension Materials Aggregation
  showExtensionBreakouts?: boolean;
  // Cost Summary Controls
  showSubtotalInCostSummary?: boolean;
  // Cost Summary Category Controls
  showExtensionMaterialsInCostSummary?: boolean;
  showQuotedMaterialsInCostSummary?: boolean;
  showLaborInCostSummary?: boolean;
  showEquipmentInCostSummary?: boolean;
  showSubcontractorsInCostSummary?: boolean;
  showGeneralExpensesInCostSummary?: boolean;
  // Notes Display
  showNotesInTables?: boolean;
  // Expiration Date
  showExpirationDate?: boolean;
  expirationDate?: Date | null;
  // Context-aware props
  isEstimateContext?: boolean;
  onPrint?: () => void;
  // User and account data
  userData?: any;
  accountData?: any;
}

// Default template for work description
const getDefaultWorkDescriptionTemplate = (): string => {
  return `
    <p><strong>We include installation of the following:</strong></p>
    <ol>
      <li>Inclusion 1</li>
      <li>Inclusion 2</li>
    </ol>
    <p><strong>We exclude the following:</strong></p>
    <ol>
      <li>Exclusion 1</li>
      <li>Exclusion 2</li>
    </ol>
    <p><strong>The following are included as alternates (VE):</strong></p>
    <ol>
      <li>Alternate 1</li>
      <li>Alternate 2</li>
    </ol>
  `;
};

export const ChangeOrderReport: React.FC<ChangeOrderReportProps> = ({
  companyName = 'Unity Build Technologies',
  workDescription = '',
  pcoNumber = '',
  showDetailedBreakdown = true,
  includeLaborRates = true,
  includeEquipmentCosts = true,
  includeSubcontractorCosts = true,
  includeGeneralExpenses = true,
  showTaxDetails = true,
  showOverheadMarkup = true,
  showBaseCostColumn = true,
  // New section visibility controls
  showMaterialsSection = true,
  showQuotesSection = true,
  showLaborSection = true,
  showProjectTotalsBreakdown = true,
  // Contractual Clauses
  showGlobalSupplyChainClause = true,
  showTariffsAndDutiesClause = true,
  showStandardContractTerms = true,
  // Extension Materials Aggregation
  showExtensionBreakouts = true,
  // Cost Summary Controls
  showSubtotalInCostSummary = true,
  // Cost Summary Category Controls
  showExtensionMaterialsInCostSummary = true,
  showQuotedMaterialsInCostSummary = true,
  showLaborInCostSummary = true,
  showEquipmentInCostSummary = true,
  showSubcontractorsInCostSummary = true,
  showGeneralExpensesInCostSummary = true,
  // Notes Display
  showNotesInTables = true,
  // Expiration Date
  showExpirationDate = false,
  expirationDate = null,
  // Context-aware props
  isEstimateContext = false,
  onPrint,
  // User and account data
  userData,
  accountData,
}) => {
  const theme = useTheme();
  const selectedEstimate = useStore(useUnityBuildStore).selectedEstimate;
  const selectedContract = useStore(useUnityBuildStore).selectedContract;
  const selectedChangeOrder = useStore(useUnityBuildStore).selectedChangeOrder;
  const selectedProject = useStore(useUnityBuildStore).selectedProject;

  // Helper function to get work description content with default template fallback
  const getWorkDescriptionContent = useCallback((): string => {
    const description =
      workDescription || selectedChangeOrder?.description || selectedEstimate?.description || '';

    // Check for empty content (null, undefined, empty string, or only whitespace)
    const hasNoContent =
      !description ||
      description === '' ||
      (typeof description === 'string' && description.trim() === '') ||
      (typeof description === 'string' && description.replace(/\s+/g, '') === '');

    if (hasNoContent) {
      return getDefaultWorkDescriptionTemplate();
    }

    return description;
  }, [workDescription, selectedChangeOrder?.description, selectedEstimate?.description]);

  // State for collapsible sections
  const [expandedSections] = useState<{ [key: string]: boolean }>({
    materials: true,
    quotes: true,
    labor: true,
    equipment: true,
    subcontractors: true,
    generalExpenses: true,
  });

  // Form state for signature fields
  const [contractorInfo, setContractorInfo] = useState({
    name: '',
    date: '',
    signature: '',
  });

  const [clientInfo, setClientInfo] = useState({
    name: '',
    date: '',
    signature: '',
    changeOrderNumber: pcoNumber,
  });

  // Get closeout data
  const { data: estimateCloseouts } = useGetTotalFinalPriceCloseouts();
  const { data: changeOrderCloseouts } = useGetTotalFinalPriceChangeOrderCloseouts();

  const closeoutData = selectedEstimate?.estimateid ? estimateCloseouts : changeOrderCloseouts;

  // Enhanced material data calculation with aggregation
  const materialBreakdown = useMemo(() => {
    if (!closeoutData?.closeouts?.extendestimate?.extendestimateList) return [];

    return getMaterialBreakdownForReport(
      closeoutData.closeouts.extendestimate.extendestimateList as any,
      !showExtensionBreakouts // Flip logic: true = show breakouts (no aggregation), false = aggregate by name
    );
  }, [closeoutData, showExtensionBreakouts]);

  // Equipment data
  const equipmentBreakdown = useMemo(() => {
    if (!closeoutData?.closeouts?.equipmentcloseout?.equipmentcloseoutList) return [];

    return closeoutData.closeouts.equipmentcloseout.equipmentcloseoutList.map(item => ({
      id: item.closeoutid || '',
      name: item.name || 'Unknown Equipment',
      quantity: item.quantity || 0,
      duration: item.duration || 0,
      unitCost: item.unitcost || 0,
      totalCost: item.totalcost || 0,
      notes: item.notes || '',
    }));
  }, [closeoutData]);

  // Subcontractor data
  const subcontractorBreakdown = useMemo(() => {
    if (!closeoutData?.closeouts?.subcontractcloseout?.subcontractcloseoutList) return [];

    return closeoutData.closeouts.subcontractcloseout.subcontractcloseoutList.map(item => ({
      id: item.closeoutid || '',
      name: item.name || 'Unknown Subcontractor',
      description: item.notes || '',
      quotedAmount: item.quotedcost || 0,
      totalCost: item.totalcost || 0,
      notes: item.notes || '',
    }));
  }, [closeoutData]);

  // Quote data
  const quoteBreakdown = useMemo(() => {
    if (!closeoutData?.closeouts?.quotecloseout?.quotecloseoutList) return [];

    return closeoutData.closeouts.quotecloseout.quotecloseoutList.map(item => ({
      id: item.closeoutid || '',
      name: item.name || 'Unknown Quote',
      quotedCost: item.quotedcost || 0,
      adjustmentPercent: item.adjustedpercent || 0,
      totalCost: item.totalcost || 0,
      notes: item.notes || '',
    }));
  }, [closeoutData]);

  // General expenses data
  const generalExpenseBreakdown = useMemo(() => {
    if (!closeoutData?.closeouts?.generalexpensecloseout?.generalexpensecloseoutList) return [];

    return closeoutData.closeouts.generalexpensecloseout.generalexpensecloseoutList.map(item => ({
      id: item.closeoutid || '',
      name: item.name || 'Unknown Expense',
      description: '',
      quantity: item.quantity || 0,
      unitCost: item.unitcost || 0,
      totalCost: item.totalcost || 0,
      notes: item.notes || '',
    }));
  }, [closeoutData]);

  // Calculate comprehensive summary
  const comprehensiveSummary = useMemo(() => {
    if (!closeoutData?.closeouts) return null;

    const { closeouts } = closeoutData;

    // Calculate base costs from item lists (matching TotalPricingRow logic)
    // These represent the sum of individual item costs before closeout-level adjustments
    const materialTotal = materialBreakdown.reduce((sum, item) => sum + item.totalMaterialCost, 0);
    const quoteTotal = quoteBreakdown.reduce((sum, item) => sum + item.totalCost, 0);
    const equipmentTotal = equipmentBreakdown.reduce((sum, item) => sum + item.totalCost, 0);
    const subcontractorTotal = subcontractorBreakdown.reduce(
      (sum, item) => sum + item.totalCost,
      0
    );
    const generalExpenseTotal = generalExpenseBreakdown.reduce(
      (sum, item) => sum + item.totalCost,
      0
    );

    // Extract actual labor data from closeouts
    const directLaborList = closeouts.directlaborcloseout?.directlaborcloseoutList || [];
    const laborFactoringList = closeouts.laborfactoringcloseout?.laborfactoringcloseoutList || [];
    const incidentalLaborList =
      closeouts.incidentallaborcloseout?.incidentallaborcloseoutList || [];
    const indirectLaborList = closeouts.indirectlaborcloseout?.indirectlaborcloseoutList || [];

    // Calculate direct labor from actual data
    const directLaborCost = directLaborList.reduce((sum, labor) => sum + (labor.totalcost || 0), 0);
    const directLaborHours = directLaborList.reduce(
      (sum, labor) => sum + (labor.allocatedhours || 0),
      0
    );
    const avgDirectLaborRate = directLaborHours > 0 ? directLaborCost / directLaborHours : 0;

    // Calculate labor factoring from actual data
    const laborFactoringCost = laborFactoringList.reduce(
      (sum, labor) => sum + (labor.totalcost || 0),
      0
    );
    const laborFactoringHours = laborFactoringList.reduce(
      (sum, labor) => sum + (labor.allocatedhours || 0),
      0
    );
    const avgLaborFactoringRate =
      laborFactoringHours > 0 ? laborFactoringCost / laborFactoringHours : 0;

    // Calculate incidental labor from actual data
    const incidentalLaborCost = incidentalLaborList.reduce(
      (sum, labor) => sum + (labor.totalcost || 0),
      0
    );
    const incidentalLaborHours = incidentalLaborList.reduce(
      (sum, labor) => sum + (labor.allocatedhours || 0),
      0
    );
    const avgIncidentalLaborRate =
      incidentalLaborHours > 0 ? incidentalLaborCost / incidentalLaborHours : 0;

    // Calculate indirect labor from actual data
    const indirectLaborCost = indirectLaborList.reduce(
      (sum, labor) => sum + (labor.totalcost || 0),
      0
    );
    const indirectLaborHours = indirectLaborList.reduce(
      (sum, labor) => sum + (labor.allocatedhours || 0),
      0
    );
    const avgIndirectLaborRate =
      indirectLaborHours > 0 ? indirectLaborCost / indirectLaborHours : 0;

    const totalBaseLaborCost =
      directLaborCost + laborFactoringCost + incidentalLaborCost + indirectLaborCost;
    const totalLaborHours =
      directLaborHours + laborFactoringHours + incidentalLaborHours + indirectLaborHours;

    // Use actual data from closeout calculations instead of hardcoded values
    const materialCloseout = closeouts.extendestimate;
    const directLaborCloseout = closeouts.directlaborcloseout;
    const laborFactoringCloseout = closeouts.laborfactoringcloseout;
    const incidentalLaborCloseout = closeouts.incidentallaborcloseout;
    const indirectLaborCloseout = closeouts.indirectlaborcloseout;
    const equipmentCloseout = closeouts.equipmentcloseout;
    const subcontractorCloseout = closeouts.subcontractcloseout;
    const generalExpenseCloseout = closeouts.generalexpensecloseout;
    const quoteCloseout = closeouts.quotecloseout;

    // Use the already calculated amounts from closeout data (matching TotalPricingRow approach)
    // Extension Materials adjustments
    const materialTax = materialCloseout?.adjustedcost || 0;
    const materialOverhead = materialCloseout?.overheadcost || 0;
    const materialMarkup = materialCloseout?.profitcost || 0;
    const adjustedMaterialTotal = materialCloseout?.totalcost || 0;

    // Labor calculations - combine all labor types
    const directLaborTotal = directLaborCloseout?.totalcost || 0;
    const laborFactoringTotal = laborFactoringCloseout?.totalcost || 0;
    const incidentalLaborTotal = incidentalLaborCloseout?.totalcost || 0;
    const indirectLaborTotal = indirectLaborCloseout?.totalcost || 0;
    const adjustedLaborTotal =
      directLaborTotal + laborFactoringTotal + incidentalLaborTotal + indirectLaborTotal;

    // Labor adjustments - combine all labor closeout adjustments
    const laborTax =
      (directLaborCloseout?.adjustedcost || 0) +
      (laborFactoringCloseout?.adjustedcost || 0) +
      (incidentalLaborCloseout?.adjustedcost || 0) +
      (indirectLaborCloseout?.adjustedcost || 0);
    const laborOverhead =
      (directLaborCloseout?.overheadcost || 0) +
      (laborFactoringCloseout?.overheadcost || 0) +
      (incidentalLaborCloseout?.overheadcost || 0) +
      (indirectLaborCloseout?.overheadcost || 0);
    const laborMarkup =
      (directLaborCloseout?.profitcost || 0) +
      (laborFactoringCloseout?.profitcost || 0) +
      (incidentalLaborCloseout?.profitcost || 0) +
      (indirectLaborCloseout?.profitcost || 0);

    // Equipment calculations using actual closeout data
    const equipmentTax = equipmentCloseout?.adjustedcost || 0;
    const equipmentOverhead = equipmentCloseout?.overheadcost || 0;
    const equipmentMarkup = equipmentCloseout?.profitcost || 0;
    const adjustedEquipmentTotal = equipmentCloseout?.totalcost || 0;

    // Subcontractor calculations using actual closeout data
    const subcontractorTax = subcontractorCloseout?.adjustedcost || 0;
    const subcontractorOverhead = subcontractorCloseout?.overheadcost || 0;
    const subcontractorMarkup = subcontractorCloseout?.profitcost || 0;
    const adjustedSubcontractorTotal = subcontractorCloseout?.totalcost || 0;

    // Quote calculations using actual closeout data
    const quoteTax = quoteCloseout?.adjustedcost || 0;
    const quoteOverhead = quoteCloseout?.overheadcost || 0;
    const quoteMarkup = quoteCloseout?.profitcost || 0;
    const adjustedQuoteTotal = quoteCloseout?.totalcost || 0;

    // General expense calculations using actual closeout data
    const generalExpenseTax = generalExpenseCloseout?.adjustedcost || 0;
    const generalExpenseOverhead = generalExpenseCloseout?.overheadcost || 0;
    const generalExpenseMarkup = generalExpenseCloseout?.profitcost || 0;
    const adjustedGeneralExpenseTotal = generalExpenseCloseout?.totalcost || 0;

    // Use actual calculated totals from the system
    const subtotal =
      closeoutData.closeouts?.totalcost ||
      adjustedMaterialTotal +
        adjustedLaborTotal +
        adjustedEquipmentTotal +
        adjustedSubcontractorTotal +
        adjustedQuoteTotal +
        adjustedGeneralExpenseTotal;

    const projectOverhead = closeoutData.closeouts?.totaloverhead || 0;
    const profit = closeoutData.closeouts?.totalprofit || 0;
    const grandTotal =
      closeoutData.closeouts?.sellingprice ||
      closeoutData.closeouts?.totalfinalprice ||
      subtotal + projectOverhead + profit;

    return {
      materialTotal,
      materialTax,
      materialOverhead,
      materialMarkup,
      adjustedMaterialTotal,
      totalLaborHours,
      directLaborCost,
      directLaborHours,
      avgDirectLaborRate,
      laborFactoringCost,
      laborFactoringHours,
      avgLaborFactoringRate,
      incidentalLaborCost,
      incidentalLaborHours,
      avgIncidentalLaborRate,
      indirectLaborCost,
      indirectLaborHours,
      avgIndirectLaborRate,
      totalBaseLaborCost,
      laborTax,
      laborOverhead,
      laborMarkup,
      adjustedLaborTotal,
      equipmentTotal,
      equipmentTax,
      equipmentOverhead,
      equipmentMarkup,
      adjustedEquipmentTotal,
      subcontractorTotal,
      subcontractorTax,
      subcontractorOverhead,
      subcontractorMarkup,
      adjustedSubcontractorTotal,
      quoteTotal,
      quoteTax,
      quoteOverhead,
      quoteMarkup,
      adjustedQuoteTotal,
      generalExpenseTotal,
      generalExpenseTax,
      generalExpenseOverhead,
      generalExpenseMarkup,
      adjustedGeneralExpenseTotal,
      subtotal,
      projectOverhead,
      profit,
      grandTotal,
    };
  }, [
    closeoutData,
    materialBreakdown,
    quoteBreakdown,
    equipmentBreakdown,
    subcontractorBreakdown,
    generalExpenseBreakdown,
  ]);

  // Inject global print styles
  useEffect(() => {
    const styleId = 'global-print-styles';
    let styleElement = document.getElementById(styleId) as HTMLStyleElement;

    if (!styleElement) {
      styleElement = document.createElement('style');
      styleElement.id = styleId;
      styleElement.textContent = GlobalPrintStyles;
      document.head.appendChild(styleElement);
    }

    // Cleanup function to remove styles when component unmounts
    return () => {
      const existingStyle = document.getElementById(styleId);
      if (existingStyle) {
        document.head.removeChild(existingStyle);
      }
    };
  }, []);

  const handlePrint = useCallback(() => {
    // Prepare data for PDF generation
    const pdfData = {
      companyName,
      workDescription: getWorkDescriptionContent(),
      pcoNumber: clientInfo.changeOrderNumber || pcoNumber,
      isEstimateContext,
      userData,
      accountData,
      projectData: selectedProject,
      contractData: selectedContract,
      estimateData: selectedEstimate,
      changeOrderData: selectedChangeOrder,
      materialBreakdown,
      quoteBreakdown,
      laborBreakdown: closeoutData?.closeouts?.directlaborcloseout?.directlaborcloseoutList || [],
      equipmentBreakdown,
      subcontractorBreakdown,
      generalExpenseBreakdown,
      comprehensiveSummary,
      expirationDate,
    };

    const pdfSettings = {
      showDetailedBreakdown,
      includeLaborRates,
      includeEquipmentCosts,
      includeSubcontractorCosts,
      includeGeneralExpenses,
      showTaxDetails,
      showOverheadMarkup,
      showBaseCostColumn,
      showMaterialsSection,
      showQuotesSection,
      showLaborSection,
      showProjectTotalsBreakdown,
      showGlobalSupplyChainClause,
      showTariffsAndDutiesClause,
      showStandardContractTerms,
      showExtensionBreakouts,
      showSubtotalInCostSummary,
      showExtensionMaterialsInCostSummary,
      showQuotedMaterialsInCostSummary,
      showLaborInCostSummary,
      showEquipmentInCostSummary,
      showSubcontractorsInCostSummary,
      showGeneralExpensesInCostSummary,
      showNotesInTables,
      showExpirationDate,
    };

    const filename = `${isEstimateContext ? 'Estimate' : 'ChangeOrder'}_${
      clientInfo.changeOrderNumber || pcoNumber || 'Report'
    }_${new Date().toISOString().split('T')[0]}.pdf`;

    downloadPDF(pdfData, pdfSettings, filename);
  }, [
    companyName,
    pcoNumber,
    isEstimateContext,
    userData,
    accountData,
    selectedProject,
    selectedContract,
    selectedEstimate,
    selectedChangeOrder,
    materialBreakdown,
    quoteBreakdown,
    equipmentBreakdown,
    subcontractorBreakdown,
    generalExpenseBreakdown,
    comprehensiveSummary,
    expirationDate,
    showDetailedBreakdown,
    includeLaborRates,
    includeEquipmentCosts,
    includeSubcontractorCosts,
    includeGeneralExpenses,
    showTaxDetails,
    showOverheadMarkup,
    showBaseCostColumn,
    showMaterialsSection,
    showQuotesSection,
    showLaborSection,
    showProjectTotalsBreakdown,
    showGlobalSupplyChainClause,
    showTariffsAndDutiesClause,
    showStandardContractTerms,
    showExtensionBreakouts,
    showSubtotalInCostSummary,
    showExtensionMaterialsInCostSummary,
    showQuotedMaterialsInCostSummary,
    showLaborInCostSummary,
    showEquipmentInCostSummary,
    showSubcontractorsInCostSummary,
    showGeneralExpensesInCostSummary,
    showNotesInTables,
    showExpirationDate,
    closeoutData,
    clientInfo.changeOrderNumber,
    getWorkDescriptionContent,
  ]);

  if (!closeoutData) {
    return (
      <Box p={4} textAlign="center">
        <Typography variant="h6">Loading closeout data...</Typography>
      </Box>
    );
  }

  const currentDate = new Date().toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });

  return (
    <ReportContainer className="report-container">
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          mb: 3,
          pb: 2,
          borderBottom: `2px solid ${theme.palette.divider}`,
          '& > *': {
            border: 'none !important',
            boxShadow: 'none !important',
          },
        }}
      >
        {/* Contractor Information - Left Side */}
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: '16px', mb: 2 }}>
            {accountData?.account?.accountname || companyName}
          </Typography>

          {/* User Information */}
          {userData?.user && (
            <Box sx={{ pt: 1, borderTop: `1px solid ${theme.palette.divider}` }}>
              <Typography variant="body2" sx={{ fontSize: '11px', fontWeight: 'bold', mb: 0.5 }}>
                Prepared by:
              </Typography>
              <Typography variant="body2" sx={{ fontSize: '11px' }}>
                {userData.user.name}
              </Typography>
              <Typography variant="body2" sx={{ fontSize: '11px', color: 'text.secondary' }}>
                {userData.user.email}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Contract & Document Information - Right Side */}
        <Box sx={{ flex: 1, textAlign: 'right' }}>
          {/* Project Address */}
          {selectedProject?.address && (
            <Typography variant="body2" sx={{ fontSize: '12px', mb: 0.5 }}>
              <strong>Project Address:</strong> {selectedProject.address}
            </Typography>
          )}

          {!isEstimateContext && (
            <>
              <Typography variant="body2" sx={{ fontSize: '12px', mb: 0.5 }}>
                <strong>Contract:</strong> {selectedContract?.contractname || 'N/A'}
              </Typography>
              <Typography variant="body2" sx={{ fontSize: '12px', mb: 0.5 }}>
                <strong>Contract #:</strong> {selectedContract?.customid || 'N/A'}
              </Typography>
            </>
          )}
          <Typography variant="body2" sx={{ fontSize: '12px', mb: 0.5 }}>
            <strong>{isEstimateContext ? 'Estimate #' : 'PCO #'}:</strong>{' '}
            {clientInfo.changeOrderNumber || pcoNumber}
          </Typography>
          <Typography variant="body2" sx={{ fontSize: '12px', mb: 0.5 }}>
            <strong>{isEstimateContext ? 'Estimate Name' : 'Change Order Name'}:</strong>{' '}
            {(isEstimateContext ? selectedEstimate?.name : selectedChangeOrder?.name) || 'N/A'}
          </Typography>

          {/* Expiration Date */}
          {showExpirationDate && expirationDate && (
            <Typography variant="body2" sx={{ fontSize: '12px', mt: 1 }}>
              <strong>Expires:</strong>{' '}
              {expirationDate.toLocaleDateString('en-US', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}
            </Typography>
          )}
        </Box>
      </Box>

      {/* Report Title and Work Description - Keep Together */}
      <Box
        className="report-title-and-description"
        sx={{
          '@media print': {
            pageBreakInside: 'avoid',
            breakInside: 'avoid',
          },
        }}
      >
        <Typography variant="h4" align="center" sx={{ fontWeight: 'bold', mb: 3 }}>
          {isEstimateContext ? 'Proposed Estimate' : 'Proposed Change Order'}
        </Typography>

        {/* Work Description */}
        <>
          <SectionHeader>
            <Typography variant="subtitle1">Work Description</Typography>
          </SectionHeader>
          <Collapse in={true}>
            <Box sx={{ mb: 2, p: 2 }}>
              <Box
                sx={{
                  fontSize: '12px',
                  minHeight: '60px',
                  maxWidth: '100%',
                  overflow: 'hidden',
                  wordWrap: 'break-word',
                  overflowWrap: 'break-word',
                  wordBreak: 'normal',
                  whiteSpace: 'normal',
                  '& p': {
                    margin: '0.5em 0',
                    wordWrap: 'break-word',
                    overflowWrap: 'break-word',
                    wordBreak: 'normal',
                    maxWidth: '100%',
                  },
                  '& h1, & h2, & h3, & h4, & h5, & h6': {
                    margin: '1em 0 0.5em 0',
                    fontWeight: 'bold',
                    wordWrap: 'break-word',
                    overflowWrap: 'break-word',
                    wordBreak: 'normal',
                    maxWidth: '100%',
                  },
                  '& h1': { fontSize: '16px' },
                  '& h2': { fontSize: '15px' },
                  '& h3': { fontSize: '14px' },
                  '& h4': { fontSize: '13px' },
                  '& ul, & ol': {
                    paddingLeft: '20px',
                    margin: '0.5em 0',
                    maxWidth: '100%',
                  },
                  '& li': {
                    margin: '0.25em 0',
                    wordWrap: 'break-word',
                    overflowWrap: 'break-word',
                    wordBreak: 'normal',
                    maxWidth: '100%',
                  },
                  '& strong': { fontWeight: 'bold' },
                  '& em': { fontStyle: 'italic' },
                  '& u': { textDecoration: 'underline' },
                  // Print-specific styles
                  '@media print': {
                    maxWidth: '100% !important',
                    overflow: 'visible !important',
                    wordWrap: 'break-word !important',
                    overflowWrap: 'break-word !important',
                    wordBreak: 'normal !important',
                    fontSize: '10px !important',
                    lineHeight: '1.4 !important',
                    padding: '0 !important',
                    margin: '0 !important',
                    '& *': {
                      color: '#333333 !important',
                      backgroundColor: 'transparent !important',
                      maxWidth: '100% !important',
                      wordWrap: 'break-word !important',
                      overflowWrap: 'break-word !important',
                      wordBreak: 'normal !important',
                      overflow: 'visible !important',
                    },
                    '& p, & li, & h1, & h2, & h3, & h4, & h5, & h6': {
                      maxWidth: '100% !important',
                      wordWrap: 'break-word !important',
                      overflowWrap: 'break-word !important',
                      wordBreak: 'normal !important',
                      overflow: 'visible !important',
                      margin: '0.3em 0 !important',
                    },
                    '& ol, & ul': {
                      paddingLeft: '15px !important',
                      maxWidth: '100% !important',
                      margin: '0.3em 0 !important',
                    },
                    '& li': {
                      margin: '0.2em 0 !important',
                      maxWidth: '100% !important',
                      wordWrap: 'break-word !important',
                      overflowWrap: 'break-word !important',
                      wordBreak: 'normal !important',
                    },
                  },
                }}
                dangerouslySetInnerHTML={{
                  __html: getWorkDescriptionContent(),
                }}
              />
            </Box>
          </Collapse>
        </>
      </Box>

      {/* Extension Materials Section */}
      {showMaterialsSection && (
        <>
          <SectionHeader>
            <Typography variant="subtitle1">Extension Materials</Typography>
          </SectionHeader>
          <Collapse in={expandedSections.materials}>
            <TableContainer sx={{ mb: 2 }}>
              <DetailTable size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Description</TableCell>
                    {showExtensionBreakouts && <TableCell>Breakout</TableCell>}
                    <TableCell align="right">Qty</TableCell>
                    <TableCell>Unit</TableCell>
                    <TableCell align="right">Unit Cost</TableCell>
                    <TableCell align="right">Total Mat. $</TableCell>
                    <TableCell align="right">Labor Hrs</TableCell>
                    <TableCell align="center">Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {materialBreakdown.map((item, index) => (
                    <TableRow key={index}>
                      <TableCell>{item.description}</TableCell>
                      {showExtensionBreakouts && <TableCell>{item.breakout}</TableCell>}
                      <TableCell align="right">{formatNumber(item.quantity)}</TableCell>
                      <TableCell>{item.unit}</TableCell>
                      <TableCell align="right">{formatCurrency(item.unitCost)}</TableCell>
                      <TableCell align="right">{formatCurrency(item.totalMaterialCost)}</TableCell>
                      <TableCell align="right">{item.totalLaborHours.toFixed(3)}</TableCell>
                      <TableCell align="center">{item.isQuoted ? 'Quoted' : 'Estimated'}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow className="total-row">
                    <TableCell colSpan={showExtensionBreakouts ? 5 : 4} sx={{ fontWeight: 'bold' }}>
                      Totals
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                      {formatCurrency(
                        materialBreakdown.reduce((sum, item) => sum + item.totalMaterialCost, 0)
                      )}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                      {materialBreakdown
                        .reduce((sum, item) => sum + item.totalLaborHours, 0)
                        .toFixed(3)}
                    </TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                </TableBody>
              </DetailTable>
            </TableContainer>
          </Collapse>
        </>
      )}

      {/* Quotes Section */}
      {showQuotesSection && quoteBreakdown.length > 0 && (
        <>
          <SectionHeader>
            <Typography variant="subtitle1">Quoted Materials</Typography>
          </SectionHeader>
          <Collapse in={expandedSections.quotes}>
            <TableContainer sx={{ mb: 2 }}>
              <DetailTable size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Description</TableCell>
                    <TableCell align="right">Quoted Cost</TableCell>
                    <TableCell align="right">Total Cost</TableCell>
                    {showNotesInTables && <TableCell>Notes</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {quoteBreakdown.map((item, index) => (
                    <TableRow key={index}>
                      <TableCell>{item.name}</TableCell>
                      <TableCell align="right">{formatCurrency(item.quotedCost)}</TableCell>
                      <TableCell align="right">{formatCurrency(item.totalCost)}</TableCell>
                      {showNotesInTables && (
                        <TableCell sx={{ fontSize: '10px' }}>{item.notes}</TableCell>
                      )}
                    </TableRow>
                  ))}
                  <TableRow className="total-row">
                    <TableCell colSpan={2} sx={{ fontWeight: 'bold' }}>
                      Total Quoted Materials
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                      {formatCurrency(
                        quoteBreakdown.reduce((sum, item) => sum + item.totalCost, 0)
                      )}
                    </TableCell>
                    {showNotesInTables && <TableCell></TableCell>}
                  </TableRow>
                </TableBody>
              </DetailTable>
            </TableContainer>
          </Collapse>
        </>
      )}

      {/* Labor Section */}
      {showLaborSection && (
        <>
          <SectionHeader>
            <Typography variant="subtitle1">Labor Details</Typography>
          </SectionHeader>
          <Collapse in={expandedSections.labor}>
            <TableContainer sx={{ mb: 2 }}>
              <DetailTable size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Labor Type</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell align="right">Hours</TableCell>
                    <TableCell align="right">Total Rate</TableCell>
                    <TableCell align="right">Fringe</TableCell>
                    <TableCell align="right">Total Cost</TableCell>
                    {showNotesInTables && <TableCell>Notes</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {closeoutData?.closeouts?.directlaborcloseout?.directlaborcloseoutList?.map(
                    (labor, index) => (
                      <TableRow key={`direct-${index}`}>
                        <TableCell>Direct Labor</TableCell>
                        <TableCell>{labor.name}</TableCell>
                        <TableCell align="right">{labor.allocatedhours.toFixed(2)}</TableCell>
                        <TableCell align="right">
                          {includeLaborRates
                            ? formatCurrency(labor.laborrate * (1 + labor.burdenpercent / 100))
                            : '-'}
                        </TableCell>
                        <TableCell align="right">{formatCurrency(labor.fringe)}</TableCell>
                        <TableCell align="right">{formatCurrency(labor.totalcost)}</TableCell>
                        {showNotesInTables && (
                          <TableCell sx={{ fontSize: '10px' }}>{labor.notes}</TableCell>
                        )}
                      </TableRow>
                    )
                  )}
                  {closeoutData?.closeouts?.laborfactoringcloseout?.laborfactoringcloseoutList?.map(
                    (labor, index) => (
                      <TableRow key={`factoring-${index}`}>
                        <TableCell>Labor Factoring</TableCell>
                        <TableCell>{labor.name}</TableCell>
                        <TableCell align="right">{labor.allocatedhours.toFixed(2)}</TableCell>
                        <TableCell align="right">
                          {includeLaborRates
                            ? formatCurrency(labor.laborrate * (1 + labor.burdenpercent / 100))
                            : '-'}
                        </TableCell>
                        <TableCell align="right">{formatCurrency(labor.fringe)}</TableCell>
                        <TableCell align="right">{formatCurrency(labor.totalcost)}</TableCell>
                        {showNotesInTables && (
                          <TableCell sx={{ fontSize: '10px' }}>{labor.notes}</TableCell>
                        )}
                      </TableRow>
                    )
                  )}
                  {closeoutData?.closeouts?.incidentallaborcloseout?.incidentallaborcloseoutList?.map(
                    (labor, index) => (
                      <TableRow key={`incidental-${index}`}>
                        <TableCell>Incidental Labor</TableCell>
                        <TableCell>{labor.name}</TableCell>
                        <TableCell align="right">{labor.allocatedhours.toFixed(2)}</TableCell>
                        <TableCell align="right">
                          {includeLaborRates
                            ? formatCurrency(labor.laborrate * (1 + labor.burdenpercent / 100))
                            : '-'}
                        </TableCell>
                        <TableCell align="right">{formatCurrency(labor.fringe)}</TableCell>
                        <TableCell align="right">{formatCurrency(labor.totalcost)}</TableCell>
                        {showNotesInTables && (
                          <TableCell sx={{ fontSize: '10px' }}>{labor.notes}</TableCell>
                        )}
                      </TableRow>
                    )
                  )}
                  {closeoutData?.closeouts?.indirectlaborcloseout?.indirectlaborcloseoutList?.map(
                    (labor, index) => (
                      <TableRow key={`indirect-${index}`}>
                        <TableCell>Indirect Labor</TableCell>
                        <TableCell>{labor.name}</TableCell>
                        <TableCell align="right">{labor.allocatedhours.toFixed(2)}</TableCell>
                        <TableCell align="right">
                          {includeLaborRates
                            ? formatCurrency(labor.laborrate * (1 + labor.burdenpercent / 100))
                            : '-'}
                        </TableCell>
                        <TableCell align="right">{formatCurrency(labor.fringe)}</TableCell>
                        <TableCell align="right">{formatCurrency(labor.totalcost)}</TableCell>
                        {showNotesInTables && (
                          <TableCell sx={{ fontSize: '10px' }}>{labor.notes}</TableCell>
                        )}
                      </TableRow>
                    )
                  )}
                  <TableRow className="total-row">
                    <TableCell colSpan={5} sx={{ fontWeight: 'bold' }}>
                      Total Labor
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                      {formatCurrency(comprehensiveSummary?.totalBaseLaborCost || 0)}
                    </TableCell>
                    {showNotesInTables && <TableCell></TableCell>}
                  </TableRow>
                </TableBody>
              </DetailTable>
            </TableContainer>
          </Collapse>
        </>
      )}

      {/* Equipment Section */}
      {includeEquipmentCosts && equipmentBreakdown.length > 0 && (
        <>
          <SectionHeader>
            <Typography variant="subtitle1">Equipment</Typography>
          </SectionHeader>
          <Collapse in={expandedSections.equipment}>
            <TableContainer sx={{ mb: 2 }}>
              <DetailTable size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Equipment Name</TableCell>
                    <TableCell align="right">Quantity</TableCell>
                    <TableCell align="right">Duration</TableCell>
                    <TableCell align="right">Unit Cost</TableCell>
                    <TableCell align="right">Total Cost</TableCell>
                    {showNotesInTables && <TableCell>Notes</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {equipmentBreakdown.map((item, index) => (
                    <TableRow key={index}>
                      <TableCell>{item.name}</TableCell>
                      <TableCell align="right">{item.quantity.toFixed(1)}</TableCell>
                      <TableCell align="right">{item.duration.toFixed(1)}</TableCell>
                      <TableCell align="right">{formatCurrency(item.unitCost)}</TableCell>
                      <TableCell align="right">{formatCurrency(item.totalCost)}</TableCell>
                      {showNotesInTables && (
                        <TableCell sx={{ fontSize: '10px' }}>{item.notes}</TableCell>
                      )}
                    </TableRow>
                  ))}
                  <TableRow className="total-row">
                    <TableCell colSpan={4} sx={{ fontWeight: 'bold' }}>
                      Total Equipment
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                      {formatCurrency(
                        equipmentBreakdown.reduce((sum, item) => sum + item.totalCost, 0)
                      )}
                    </TableCell>
                    {showNotesInTables && <TableCell></TableCell>}
                  </TableRow>
                </TableBody>
              </DetailTable>
            </TableContainer>
          </Collapse>
        </>
      )}

      {/* Subcontractors Section */}
      {includeSubcontractorCosts && subcontractorBreakdown.length > 0 && (
        <>
          <SectionHeader>
            <Typography variant="subtitle1">Subcontractors</Typography>
          </SectionHeader>
          <Collapse in={expandedSections.subcontractors}>
            <TableContainer sx={{ mb: 2 }}>
              <DetailTable size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Subcontractor</TableCell>
                    <TableCell align="right">Quoted Amount</TableCell>
                    <TableCell align="right">Total Cost</TableCell>
                    {showNotesInTables && <TableCell>Notes</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {subcontractorBreakdown.map((item, index) => (
                    <TableRow key={index}>
                      <TableCell>{item.name}</TableCell>
                      <TableCell align="right">{formatCurrency(item.quotedAmount)}</TableCell>
                      <TableCell align="right">{formatCurrency(item.totalCost)}</TableCell>
                      {showNotesInTables && (
                        <TableCell sx={{ fontSize: '10px' }}>{item.notes}</TableCell>
                      )}
                    </TableRow>
                  ))}
                  <TableRow className="total-row">
                    <TableCell colSpan={2} sx={{ fontWeight: 'bold' }}>
                      Total Subcontractors
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                      {formatCurrency(
                        subcontractorBreakdown.reduce((sum, item) => sum + item.totalCost, 0)
                      )}
                    </TableCell>
                    {showNotesInTables && <TableCell></TableCell>}
                  </TableRow>
                </TableBody>
              </DetailTable>
            </TableContainer>
          </Collapse>
        </>
      )}

      {/* General Expenses Section */}
      {includeGeneralExpenses && generalExpenseBreakdown.length > 0 && (
        <>
          <SectionHeader>
            <Typography variant="subtitle1">General Expenses</Typography>
          </SectionHeader>
          <Collapse in={expandedSections.generalExpenses}>
            <TableContainer sx={{ mb: 2 }}>
              <DetailTable size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Description</TableCell>
                    <TableCell align="right">Quantity</TableCell>
                    <TableCell align="right">Unit Cost</TableCell>
                    <TableCell align="right">Total Cost</TableCell>
                    {showNotesInTables && <TableCell>Notes</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {generalExpenseBreakdown.map((item, index) => (
                    <TableRow key={index}>
                      <TableCell>{item.name}</TableCell>
                      <TableCell align="right">{item.quantity.toFixed(1)}</TableCell>
                      <TableCell align="right">{formatCurrency(item.unitCost)}</TableCell>
                      <TableCell align="right">{formatCurrency(item.totalCost)}</TableCell>
                      {showNotesInTables && (
                        <TableCell sx={{ fontSize: '10px' }}>{item.notes}</TableCell>
                      )}
                    </TableRow>
                  ))}
                  <TableRow className="total-row">
                    <TableCell colSpan={3} sx={{ fontWeight: 'bold' }}>
                      Total General Expenses
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                      {formatCurrency(
                        generalExpenseBreakdown.reduce((sum, item) => sum + item.totalCost, 0)
                      )}
                    </TableCell>
                    {showNotesInTables && <TableCell></TableCell>}
                  </TableRow>
                </TableBody>
              </DetailTable>
            </TableContainer>
          </Collapse>
        </>
      )}

      {/* Comprehensive Summary */}
      <>
        {comprehensiveSummary && (
          <>
            <SectionHeader>
              <Typography variant="subtitle1">Cost Summary</Typography>
            </SectionHeader>
            <TableContainer sx={{ mb: 2 }}>
              <DetailTable size="small">
                {/* Show detailed header only if any category toggles are enabled */}
                {(showExtensionMaterialsInCostSummary ||
                  showQuotedMaterialsInCostSummary ||
                  showLaborInCostSummary ||
                  showEquipmentInCostSummary ||
                  showSubcontractorsInCostSummary ||
                  showGeneralExpensesInCostSummary) && (
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>Cost Category</TableCell>
                      {showBaseCostColumn && (
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                          Base Cost
                        </TableCell>
                      )}
                      {(showTaxDetails || showOverheadMarkup) && (
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                          {showTaxDetails && showOverheadMarkup
                            ? 'Tax & Markup'
                            : showTaxDetails
                              ? 'Tax'
                              : 'Markup'}
                        </TableCell>
                      )}
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                        Total Cost
                      </TableCell>
                    </TableRow>
                  </TableHead>
                )}
                <TableBody>
                  {/* Extension Materials Row - Conditionally shown */}
                  {showExtensionMaterialsInCostSummary && (
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>Extension Materials</TableCell>
                      {showBaseCostColumn && (
                        <TableCell align="right">
                          {formatCurrency(
                            comprehensiveSummary.materialTotal +
                              (showTaxDetails ? 0 : comprehensiveSummary.materialTax || 0)
                          )}
                        </TableCell>
                      )}
                      {(showTaxDetails || showOverheadMarkup) && (
                        <TableCell align="right">
                          {showTaxDetails && (
                            <Box component="span" sx={{ display: 'block', fontSize: '10px' }}>
                              Tax: {formatCurrency(comprehensiveSummary.materialTax || 0)}
                            </Box>
                          )}
                          {showOverheadMarkup && (
                            <>
                              <Box component="span" sx={{ display: 'block', fontSize: '10px' }}>
                                Overhead:{' '}
                                {formatCurrency(comprehensiveSummary.materialOverhead || 0)}
                              </Box>
                              <Box component="span" sx={{ display: 'block', fontSize: '10px' }}>
                                Markup: {formatCurrency(comprehensiveSummary.materialMarkup || 0)}
                              </Box>
                            </>
                          )}
                        </TableCell>
                      )}
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                        {formatCurrency(comprehensiveSummary.adjustedMaterialTotal)}
                      </TableCell>
                    </TableRow>
                  )}

                  {/* Quoted Materials Row - Conditionally shown */}
                  {showQuotedMaterialsInCostSummary && comprehensiveSummary.quoteTotal > 0 && (
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>Quoted Materials</TableCell>
                      {showBaseCostColumn && (
                        <TableCell align="right">
                          {formatCurrency(
                            comprehensiveSummary.quoteTotal +
                              (showTaxDetails ? 0 : comprehensiveSummary.quoteTax || 0)
                          )}
                        </TableCell>
                      )}
                      {(showTaxDetails || showOverheadMarkup) && (
                        <TableCell align="right">
                          {showTaxDetails && (
                            <Box component="span" sx={{ display: 'block', fontSize: '10px' }}>
                              Tax: {formatCurrency(comprehensiveSummary.quoteTax || 0)}
                            </Box>
                          )}
                          {showOverheadMarkup && (
                            <>
                              <Box component="span" sx={{ display: 'block', fontSize: '10px' }}>
                                Overhead: {formatCurrency(comprehensiveSummary.quoteOverhead || 0)}
                              </Box>
                              <Box component="span" sx={{ display: 'block', fontSize: '10px' }}>
                                Markup: {formatCurrency(comprehensiveSummary.quoteMarkup || 0)}
                              </Box>
                            </>
                          )}
                        </TableCell>
                      )}
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                        {formatCurrency(comprehensiveSummary.adjustedQuoteTotal)}
                      </TableCell>
                    </TableRow>
                  )}

                  {/* Labor Row - Conditionally shown */}
                  {showLaborInCostSummary && (
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>
                        Labor
                        {showDetailedBreakdown && (
                          <Box
                            component="span"
                            sx={{
                              display: 'block',
                              fontSize: '10px',
                              fontWeight: 'normal',
                              color: 'text.secondary',
                            }}
                          >
                            Total Hours: {comprehensiveSummary.totalLaborHours.toFixed(1)}
                            {includeLaborRates && comprehensiveSummary.totalLaborHours > 0 && (
                              <>
                                {' '}
                                • Avg Rate:{' '}
                                {formatCurrency(
                                  (comprehensiveSummary.totalBaseLaborCost +
                                    (comprehensiveSummary.laborTax || 0)) /
                                    comprehensiveSummary.totalLaborHours
                                )}
                              </>
                            )}
                          </Box>
                        )}
                      </TableCell>
                      {showBaseCostColumn && (
                        <TableCell align="right">
                          {formatCurrency(
                            comprehensiveSummary.totalBaseLaborCost +
                              (comprehensiveSummary.laborTax || 0)
                          )}
                        </TableCell>
                      )}
                      {(showTaxDetails || showOverheadMarkup) && (
                        <TableCell align="right">
                          {/* Labor is non-taxable, so no tax shown */}
                          {showOverheadMarkup && (
                            <>
                              <Box component="span" sx={{ display: 'block', fontSize: '10px' }}>
                                Overhead: {formatCurrency(comprehensiveSummary.laborOverhead || 0)}
                              </Box>
                              <Box component="span" sx={{ display: 'block', fontSize: '10px' }}>
                                Markup: {formatCurrency(comprehensiveSummary.laborMarkup || 0)}
                              </Box>
                            </>
                          )}
                        </TableCell>
                      )}
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                        {formatCurrency(comprehensiveSummary.adjustedLaborTotal)}
                      </TableCell>
                    </TableRow>
                  )}

                  {/* Equipment Row - Conditionally shown */}
                  {showEquipmentInCostSummary && comprehensiveSummary.equipmentTotal > 0 && (
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>Equipment</TableCell>
                      {showBaseCostColumn && (
                        <TableCell align="right">
                          {formatCurrency(
                            comprehensiveSummary.equipmentTotal +
                              (showTaxDetails ? 0 : comprehensiveSummary.equipmentTax || 0)
                          )}
                        </TableCell>
                      )}
                      {(showTaxDetails || showOverheadMarkup) && (
                        <TableCell align="right">
                          {showTaxDetails && (
                            <Box component="span" sx={{ display: 'block', fontSize: '10px' }}>
                              Tax: {formatCurrency(comprehensiveSummary.equipmentTax || 0)}
                            </Box>
                          )}
                          {showOverheadMarkup && (
                            <>
                              <Box component="span" sx={{ display: 'block', fontSize: '10px' }}>
                                Overhead:{' '}
                                {formatCurrency(comprehensiveSummary.equipmentOverhead || 0)}
                              </Box>
                              <Box component="span" sx={{ display: 'block', fontSize: '10px' }}>
                                Markup: {formatCurrency(comprehensiveSummary.equipmentMarkup || 0)}
                              </Box>
                            </>
                          )}
                        </TableCell>
                      )}
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                        {formatCurrency(comprehensiveSummary.adjustedEquipmentTotal)}
                      </TableCell>
                    </TableRow>
                  )}

                  {/* Subcontractors Row - Conditionally shown */}
                  {showSubcontractorsInCostSummary &&
                    comprehensiveSummary.subcontractorTotal > 0 && (
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold' }}>Subcontractors</TableCell>
                        {showBaseCostColumn && (
                          <TableCell align="right">
                            {formatCurrency(
                              comprehensiveSummary.subcontractorTotal +
                                (comprehensiveSummary.subcontractorTax || 0)
                            )}
                          </TableCell>
                        )}
                        {(showTaxDetails || showOverheadMarkup) && (
                          <TableCell align="right">
                            {/* Subcontractors are non-taxable, so no tax shown */}
                            {showOverheadMarkup && (
                              <>
                                <Box component="span" sx={{ display: 'block', fontSize: '10px' }}>
                                  Overhead:{' '}
                                  {formatCurrency(comprehensiveSummary.subcontractorOverhead || 0)}
                                </Box>
                                <Box component="span" sx={{ display: 'block', fontSize: '10px' }}>
                                  Markup:{' '}
                                  {formatCurrency(comprehensiveSummary.subcontractorMarkup || 0)}
                                </Box>
                              </>
                            )}
                          </TableCell>
                        )}
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                          {formatCurrency(comprehensiveSummary.adjustedSubcontractorTotal)}
                        </TableCell>
                      </TableRow>
                    )}

                  {/* General Expenses Row - Conditionally shown */}
                  {showGeneralExpensesInCostSummary &&
                    comprehensiveSummary.generalExpenseTotal > 0 && (
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold' }}>General Expenses</TableCell>
                        {showBaseCostColumn && (
                          <TableCell align="right">
                            {formatCurrency(
                              comprehensiveSummary.generalExpenseTotal +
                                (comprehensiveSummary.generalExpenseTax || 0)
                            )}
                          </TableCell>
                        )}
                        {(showTaxDetails || showOverheadMarkup) && (
                          <TableCell align="right">
                            {/* General Expenses are non-taxable, so no tax shown */}
                            {showOverheadMarkup && (
                              <>
                                <Box component="span" sx={{ display: 'block', fontSize: '10px' }}>
                                  Overhead:{' '}
                                  {formatCurrency(comprehensiveSummary.generalExpenseOverhead || 0)}
                                </Box>
                                <Box component="span" sx={{ display: 'block', fontSize: '10px' }}>
                                  Markup:{' '}
                                  {formatCurrency(comprehensiveSummary.generalExpenseMarkup || 0)}
                                </Box>
                              </>
                            )}
                          </TableCell>
                        )}
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                          {formatCurrency(comprehensiveSummary.adjustedGeneralExpenseTotal)}
                        </TableCell>
                      </TableRow>
                    )}

                  {/* Subtotal Row - conditionally shown */}
                  {showSubtotalInCostSummary && (
                    <TableRow className="total-row">
                      <TableCell sx={{ fontWeight: 'bold', fontSize: '14px' }}>Subtotal</TableCell>
                      {showBaseCostColumn && <TableCell></TableCell>}
                      {(showTaxDetails || showOverheadMarkup) && <TableCell></TableCell>}
                      <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '14px' }}>
                        {formatCurrency(comprehensiveSummary.subtotal)}
                      </TableCell>
                    </TableRow>
                  )}

                  {/* Project Level Adjustments - only show when subtotal is shown */}
                  {showProjectTotalsBreakdown && showSubtotalInCostSummary && (
                    <>
                      {comprehensiveSummary.projectOverhead > 0 && (
                        <TableRow>
                          <TableCell sx={{ pl: 4 }}>Project Overhead</TableCell>
                          {showBaseCostColumn && <TableCell></TableCell>}
                          {(showTaxDetails || showOverheadMarkup) && <TableCell></TableCell>}
                          <TableCell align="right">
                            {formatCurrency(comprehensiveSummary.projectOverhead)}
                          </TableCell>
                        </TableRow>
                      )}
                      {comprehensiveSummary.profit > 0 && (
                        <TableRow>
                          <TableCell sx={{ pl: 4 }}>Project Profit</TableCell>
                          {showBaseCostColumn && <TableCell></TableCell>}
                          {(showTaxDetails || showOverheadMarkup) && <TableCell></TableCell>}
                          <TableCell align="right">
                            {formatCurrency(comprehensiveSummary.profit)}
                          </TableCell>
                        </TableRow>
                      )}
                    </>
                  )}

                  {/* Final Total Row */}
                  <TableRow className="total-row">
                    <TableCell sx={{ fontWeight: 'bold', fontSize: '16px' }}>
                      {closeoutData.closeouts?.sellingprice ? 'SELLING PRICE' : 'TOTAL AMOUNT'}
                    </TableCell>
                    {showBaseCostColumn && <TableCell></TableCell>}
                    {(showTaxDetails || showOverheadMarkup) && <TableCell></TableCell>}
                    <TableCell
                      align="right"
                      sx={{ fontWeight: 'bold', fontSize: '16px', color: 'primary.main' }}
                    >
                      {formatCurrency(
                        closeoutData.closeouts?.sellingprice || comprehensiveSummary.grandTotal
                      )}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </DetailTable>
            </TableContainer>
          </>
        )}
      </>

      {/* Global Supply Chain Distribution Clause */}
      {showGlobalSupplyChainClause && (
        <Box
          sx={{
            '@media print': {
              pageBreakInside: 'avoid',
              breakInside: 'avoid',
            },
          }}
        >
          <GlobalSupplyChainClause />
        </Box>
      )}

      {/* Tariffs, Duties, and Import Fees Clause */}
      {showTariffsAndDutiesClause && (
        <Box
          sx={{
            '@media print': {
              pageBreakInside: 'avoid',
              breakInside: 'avoid',
            },
          }}
        >
          <TariffsAndDutiesClause />
        </Box>
      )}

      {/* Standard Contract Terms & Conditions */}
      {showStandardContractTerms && (
        <Box
          sx={{
            '@media print': {
              pageBreakInside: 'avoid',
              breakInside: 'avoid',
            },
          }}
        >
          <StandardContractTerms />
        </Box>
      )}

      {/* Contractor Certification */}
      <ContractorCertification
        contractorInfo={contractorInfo}
        setContractorInfo={setContractorInfo}
      />

      {/* Client Acceptance */}
      <ClientAcceptance
        isEstimateContext={isEstimateContext}
        clientInfo={clientInfo}
        comprehensiveSummary={comprehensiveSummary}
        setClientInfo={setClientInfo}
      />

      {/* Footer */}
      <Box mt={4} pt={2} borderTop={`1px solid ${theme.palette.divider}`} textAlign="center">
        <Typography variant="caption" sx={{ fontSize: '10px' }}>
          Report Generated: {currentDate}
        </Typography>
      </Box>

      {/* Print Button */}
      <Box mt={3} textAlign="center" className="no-print">
        <Button
          variant="contained"
          onClick={handlePrint}
          startIcon={<GetAppIcon />}
          sx={{
            backgroundColor: theme.palette.primary.main,
            '&:hover': { backgroundColor: theme.palette.primary.dark },
          }}
        >
          Download PDF
        </Button>
      </Box>
    </ReportContainer>
  );
};
